import { ConditionallyRenderLoginSignupButtons } from "@circle-react-shared/ConditionallyRenderLoginSignupButtons";
import {
  HEADER_AUTH_BUTTON_ACTIVE_STATES_CLASS,
  HEADER_AUTH_BUTTON_THEME_VARS,
} from "@circle-react-shared/uikit/Header/headerThemeVars";
import { BlockInViewOnlyMode } from "@circle-react/components/ViewOnlyMasquerading/BlockInViewOnlyMode";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { cssAnchorProps } from "@circle-react/helpers/cssAnchorHelpers";
import { useIsCustomColorTheme } from "@circle-react/hooks/useIsCustomColorTheme";

import { LOGGED_OUT } from "@/react/components/SettingsApp/Navigation/AddLink/constants";
import type { HeaderNavAudienceApiParam } from "@/react/components/SettingsApp/Navigation/hooks/useFetchHeaderNavItems";
import { classNames } from "@/react/helpers/twMergeWithCN";

import { ProfileDropdown } from "../ProfileDropdown";
import { SearchButton } from "../SearchButton";
import { Bookmarks } from "./Bookmarks";
import { ChatThreads } from "./ChatThreads";
import { Connect } from "./Connect";
import { DirectMessages } from "./DirectMessages";
import { LoggedOutPreviewActions } from "./LoggedOutPreviewActions";
import { MemberChecklist } from "./MemberChecklist";
import { Notifications } from "./Notifications";

interface RightActionBlockProps {
  isPreviewMode?: boolean;
  previewAudience?: HeaderNavAudienceApiParam;
}

export const RightActionBlock = ({
  isPreviewMode = false,
  previewAudience,
}: RightActionBlockProps) => {
  const { currentCommunityMember, currentCommunity } = usePunditUserContext();
  const isLoggedOutPreview = isPreviewMode && previewAudience === LOGGED_OUT;
  // Default theme opts out of community branding entirely,
  // falling back to Compass's own default tokens
  const isCustomColorTheme = useIsCustomColorTheme();

  if (isLoggedOutPreview) {
    return (
      <div
        className={classNames(
          "flex items-center justify-end gap-4",
          isCustomColorTheme && HEADER_AUTH_BUTTON_ACTIVE_STATES_CLASS,
          "pointer-events-none",
        )}
        style={isCustomColorTheme ? HEADER_AUTH_BUTTON_THEME_VARS : undefined}
      >
        {!currentCommunity?.is_private && <SearchButton isPreviewMode />}
        <LoggedOutPreviewActions />
      </div>
    );
  }

  if (!currentCommunityMember) {
    return (
      <div
        className={classNames(
          "flex items-center justify-end gap-4",
          isCustomColorTheme && HEADER_AUTH_BUTTON_ACTIVE_STATES_CLASS,
        )}
        style={isCustomColorTheme ? HEADER_AUTH_BUTTON_THEME_VARS : undefined}
      >
        {!currentCommunity?.is_private && <SearchButton />}

        <ConditionallyRenderLoginSignupButtons />
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2"
      data-testid="header-v4-right-action-block"
      {...cssAnchorProps({ anchor: "app-topbar-actions" })}
    >
      <SearchButton isPreviewMode={isPreviewMode} />
      <BlockInViewOnlyMode>
        <div
          className={classNames("flex items-center gap-2", {
            "pointer-events-none": isPreviewMode,
          })}
        >
          <Notifications />
          <DirectMessages />
          <ChatThreads />
          <Connect />
          <Bookmarks />
          <MemberChecklist />
        </div>
      </BlockInViewOnlyMode>
      <ProfileDropdown isPreviewMode={isPreviewMode} />
    </div>
  );
};
